import {
  CircularProgress,
  IconButton,
  makeStyles,
  Typography,
  Link as MaterialLink,
} from '@material-ui/core';
import { useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { Founder } from '../../api/founders';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import { fullName } from '../../utils/String';
import { getColorStatus } from '../../utils/functions';
import { Avatar, Link, StatusBadge, Text } from '../common';
import FoundersRowActions from '../founders/founders-row-actions';

const useStyles = makeStyles(() => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 20,
    backgroundColor: COLORS.COLOR_ADDITIONAL_WHITE,
    borderRadius: 8,
    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 16,
  },
  cardTitle: {
    fontSize: 18,
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',

    '& > a': {
      color: COLORS.COLOR_TEXT_BASE,
      textDecoration: 'none',

      '& > span, & > svg': {
        verticalAlign: 'middle',
      },
    },
  },
  cardContentWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  cardContent: {
    display: 'flex',
    gap: 16,
  },
  description: {
    display: '-webkit-box',
    width: '100%',
    WebkitLineClamp: 4,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    wordBreak: 'break-word',
    lineHeight: '1.5',
  },
  iconsBlock: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,

    '& > a': {
      padding: 4,
    },
  },
  actions: {
    marginLeft: 'auto',
  },
  iconBox: {
    width: 27,
    height: 27,
    borderRadius: 4,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: COLORS.COLOR_BLUE_BASE,
    fontSize: 18,
    fontWeight: 'bold',
    color: COLORS.COLOR_ADDITIONAL_WHITE,
  },
  centerText: {
    transform: 'translateY(-1.5px)',
  },
  iconButton: {
    border: 'none',

    '&:hover': {
      border: 'none',
      marginBottom: 0,
    },
  },
  ventureContainer: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    gap: 8,
  },
  ventureLink: {
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

function FounderCard({
  founder,
  cardId,
  logo,
  pathname,
  actions,
}: {
  founder: Founder;
  cardId: string;
  logo?: string | null;
  pathname?: string;
  actions?: React.ReactNode;
}) {
  const classes = useStyles();
  const history = useHistory();
  const location = useLocation();

  const prevPath = `${location.pathname}${location.search}`;

  const handleCardClick = useCallback(() => {
    history.push({
      pathname:
        pathname ||
        getRoutePath(Pages.FOUNDER_DETAILS, {
          founderId: founder.id,
        }),
      state: { prevPath },
    });
  }, [history, pathname, founder.id, prevPath]);

  return (
    <div className={classes.card} id={cardId}>
      <div className={classes.cardHeader} onClick={handleCardClick}>
        <Typography variant='h5' className={classes.cardTitle}>
          <span>{fullName(founder.firstName, founder.lastName)}</span>
        </Typography>
        <div>
          <StatusBadge
            status={founder.status}
            variant={getColorStatus(founder.status)}
          />
        </div>
      </div>
      <div className={classes.cardContentWrapper}>
        <div className={classes.cardContent} onClick={handleCardClick}>
          <Avatar
            src={logo}
            name={`${founder.firstName[0]}${founder.lastName[0]}`}
            size='80'>
            {typeof logo === 'string' && !logo ? (
              <CircularProgress size={18} color='inherit' />
            ) : undefined}
          </Avatar>

          {!!founder.about && (
            <Text variant='normal' className={classes.description}>
              {founder.about}
            </Text>
          )}
        </div>
        {!!founder?.primaryVenture && (
          <div className={classes.ventureContainer}>
            <Text variant='normal'>Venture:</Text>
            <Text variant='normal' className={classes.ventureLink}>
              <MaterialLink
                component={Link}
                to={{
                  pathname: getRoutePath(Pages.VENTURE_DETAILS, {
                    ventureId: founder.primaryVentureId,
                  }),
                  search: `?pfid=${founder.id}`,
                  state: { prevPath },
                }}>
                {founder.primaryVenture}
              </MaterialLink>
            </Text>
          </div>
        )}
      </div>
      <div className={classes.iconsBlock}>
        {founder.email && (
          <IconButton
            data-testid='founder-card-email-icon'
            className={classes.iconButton}
            component={MaterialLink}
            href={`mailto:${founder.email}`}>
            <div className={classes.iconBox}>
              <div className={classes.centerText}>@</div>
            </div>
          </IconButton>
        )}
        <div className={classes.actions}>
          {actions || <FoundersRowActions founder={founder} variant='list' />}
        </div>
      </div>
    </div>
  );
}

export default FounderCard;
